// utils/consent.js — cookie/storage consent state (GDPR/ePrivacy, UK PECR, CNIL, CCPA/GPC)
//
// One choice is stored per browser and reused until it expires (CONSENT_MAX_AGE_DAYS)
// or CONSENT_VERSION is bumped (bump it whenever you add a new non-essential tool).
// Nothing non-essential (Google Analytics, anonymous visitor tracking) may run until
// hasConsent('analytics') is true.

export const GA_ID = 'G-F44V4G95W6';
export const CONSENT_KEY = 'tunasignal_cookie_consent';
export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_DAYS = 180; // CNIL: re-ask about every 6 months

const EVT_CHANGE = 'ts:consent';
const EVT_OPEN = 'ts:open-cookie-settings';

export function getConsent() {
  try {
    const c = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
    if (!c || c.v !== CONSENT_VERSION) return null;
    if (Date.now() - c.ts > CONSENT_MAX_AGE_DAYS * 864e5) return null;
    return c;
  } catch (e) {
    return null;
  }
}

export function hasConsent(category) {
  const c = getConsent();
  return !!(c && c[category]);
}

export function setConsent({ analytics, source = 'banner' }) {
  const c = { v: CONSENT_VERSION, ts: Date.now(), analytics: !!analytics, source };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(c)); } catch (e) {}
  if (!c.analytics) disableAnalytics();
  window.dispatchEvent(new CustomEvent(EVT_CHANGE, { detail: c }));
  return c;
}

// Calls cb now (if a choice exists) and on every later change. Returns unsubscribe.
export function onConsentChange(cb) {
  const current = getConsent();
  if (current) cb(current);
  const handler = (e) => cb(e.detail);
  window.addEventListener(EVT_CHANGE, handler);
  return () => window.removeEventListener(EVT_CHANGE, handler);
}

// Use from any footer link: <button onClick={openCookieSettings}>Cookie settings</button>
export function openCookieSettings() {
  window.dispatchEvent(new Event(EVT_OPEN));
}
export const OPEN_SETTINGS_EVENT = EVT_OPEN;

// Global Privacy Control (CCPA/CPRA + several US states treat it as an opt-out).
export function gpcEnabled() {
  try { return navigator.globalPrivacyControl === true; } catch (e) { return false; }
}

export function loadGoogleAnalytics() {
  if (typeof window === 'undefined' || window.__tsGaLoaded) return;
  window[`ga-disable-${GA_ID}`] = false;
  window.__tsGaLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_ID, {
    allow_google_signals: false,              // no cross-device / ads features
    allow_ad_personalization_signals: false,  // keeps GA a "service provider" under CCPA
    cookie_expires: 60 * 60 * 24 * 395,       // 13 months max (CNIL)
  });
}

function disableAnalytics() {
  window[`ga-disable-${GA_ID}`] = true;
  try {
    const host = window.location.hostname;
    const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`];
    document.cookie.split(';').map((c) => c.split('=')[0].trim())
      .filter((n) => n === '_ga' || n.startsWith('_ga_') || n === '_gid')
      .forEach((n) => domains.forEach((d) => {
        document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`;
      }));
  } catch (e) {}
}
